import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PlanverseWordmark from '../components/orbi/PlanverseWordmark';
import PlanverseSearchBar from '../components/orbi/PlanverseSearchBar';
import PlanverseCard from '../components/orbi/PlanverseCard';
import { listPlans, type PlanversePlan } from '../lib/planverse';

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function Planverse() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [plans, setPlans] = useState<PlanversePlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPlans(query).then((next) => {
      if (!cancelled) {
        setPlans(next);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [query]);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-10 font-display md:py-14">
      <header className="flex flex-col items-center gap-6">
        <PlanverseWordmark className="text-4xl md:text-5xl" size={52} />
        <PlanverseSearchBar value={query} onChange={setQuery} />
      </header>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div className="aspect-[4/3] w-full animate-pulse rounded-lg bg-muted" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border py-16 text-center">
          <p className="text-base text-foreground">No plans match "{query}"</p>
          <p className="text-sm text-muted-foreground">Try a different search or clear the filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <PlanverseCard
              key={plan.id}
              title={plan.title}
              imageUrl={plan.coverUrl}
              timestamp={timeAgo(plan.publishedAt)}
              authorInitial={plan.author.initial}
              likes={plan.likes}
              duplicates={plan.duplicates}
              views={plan.views}
              onClick={() => navigate(`/planverse/${plan.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
